import React from 'react';
import cx from 'classnames';
import { makeStyles } from 'tss-react/mui';

import { Grid, Link, Paper, Typography, units } from '@ha/dsl';
import { LazyComponent } from 'ha/components.legacy/LazyComponent';
import { ContentfulImg } from 'ha/modules/Contentful/components/ContentfulImg';

import { useSelector } from 'react-redux';
import { getCurrentLanguageCode } from 'ha/modules/LanguageSwitcher/selectors';
import { ValuePropositionData } from './types';

const useStyles = makeStyles()(theme => ({
  container: {
    padding: `0 ${units.rem(0.5)}`,
    marginBottom: units.rem(10),
    [theme.breakpoints.down('md')]: {
      marginTop: units.rem(2.5),
      marginBottom: units.rem(6.25),
      padding: `0 ${units.rem(1.5)}`,
    },
  },
  wrapper: {
    marginTop: units.rem(7.5),
    maxWidth: `${units.rem(60)} !important`,
    [theme.breakpoints.down('md')]: {
      marginTop: '0',
      marginBottom: units.rem(3),
    },
  },
  text: {
    marginBottom: units.rem(1.5),
    [theme.breakpoints.down('md')]: {
      marginBottom: units.rem(1),
    },
  },
  paper: {
    height: units.rem(18.75),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: units.rem(13.75),
      marginBottom: '16px',
    },
  },
  paperEven: {
    boxShadow: '-20px 24px 40px 0 rgba(45, 70, 88, 0.08)',
    backgroundImage:
      'linear-gradient(225deg, rgba(45, 70, 88, 0.05) 0%, rgba(45, 70, 88, 0.01) 100%)',
    [theme.breakpoints.up('md')]: {
      marginLeft: units.rem(5),
    },
  },
  paperOdd: {
    boxShadow: '20px 24px 40px 0 rgba(45, 70, 88, 0.08)',
    backgroundImage:
      'linear-gradient(135deg, rgba(45, 70, 88, 0.05) 0%, rgba(45, 70, 88, 0.01) 100%)',
    [theme.breakpoints.up('md')]: {
      marginRight: units.rem(5),
    },
  },
  textBlockOdd: {
    paddingLeft: units.rem(1),
    [theme.breakpoints.up('md')]: {
      paddingLeft: 0,
    },
  },
  textBlockEven: {
    paddingRight: units.rem(1),
    [theme.breakpoints.up('md')]: {
      paddingRight: 0,
    },
  },
  imgContainer: {
    height: '100%',
    overflow: 'hidden',
  },
}));

export interface Props {
  valuePropositions: ValuePropositionData[];
  isMobile: boolean;
}

export const ValuePropositions: React.FC<Props> = ({
  valuePropositions = [],
  isMobile,
}) => {
  const { classes } = useStyles();
  const lang = useSelector(getCurrentLanguageCode);

  return (
    <Grid
      container
      alignItems="center"
      direction="column"
      className={classes.container}
    >
      {valuePropositions.map(
        (
          { title, description, image: img, imageAsset, linkUrl, linkTitle },
          index,
        ) => {
          const isOdd = index % 2 !== 0;
          // Remove isString check after https://github.com/housinganywhere/src/pull/1505 got merged.
          const image = imageAsset || { fields: { url: img } };
          const desc = image.fields.description;
          const alt = desc ? desc[lang] || desc.en : '';

          return isMobile ? (
            <Grid
              key={title}
              container
              item
              direction="column"
              justifyContent="center"
              className={classes.wrapper}
            >
              <Typography.H1
                component="h3"
                className={classes.text}
                data-test-locator="ValueProposition Title"
              >
                {title}
              </Typography.H1>
              <Paper
                className={cx(
                  classes.paper,
                  isOdd ? classes.paperOdd : classes.paperEven,
                )}
              >
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  className={classes.imgContainer}
                >
                  <div>
                    <LazyComponent
                      bottomOffset="-200px"
                      scrollableAncestor="window"
                    >
                      <ContentfulImg
                        src={image.fields.url}
                        alt={alt}
                        defaultSrc={image.fields.url}
                        data-test-locator="ValueProposition Image"
                        sizes={{ md: '400px' }}
                      />
                    </LazyComponent>
                  </div>
                </Grid>
              </Paper>
              <Typography.Paragraph
                component="p"
                data-test-locator="ValueProposition Description"
                className={classes.text}
              >
                {description}
              </Typography.Paragraph>

              {linkUrl && linkTitle && (
                <Link to={linkUrl}>
                  <Typography.Paragraph
                    color="mint"
                    textTransform="uppercase"
                    component="p"
                    data-test-locator="ValueProposition Link"
                  >
                    {linkTitle}
                  </Typography.Paragraph>
                </Link>
              )}
            </Grid>
          ) : (
            <Grid
              key={title}
              container
              item
              alignItems="flex-start"
              className={classes.wrapper}
              direction={isOdd ? 'row-reverse' : 'row'}
            >
              <Grid
                item
                xs={6}
                className={isOdd ? classes.textBlockOdd : classes.textBlockEven}
              >
                <Typography.H1
                  component="h3"
                  className={classes.text}
                  data-test-locator="ValueProposition Title"
                >
                  {title}
                </Typography.H1>
                <Typography.Paragraph
                  component="p"
                  data-test-locator="ValueProposition Description"
                  className={classes.text}
                >
                  {description}
                </Typography.Paragraph>

                {linkUrl && linkTitle && (
                  <Link to={linkUrl}>
                    <Typography.Paragraph
                      color="mint"
                      textTransform="uppercase"
                      component="p"
                      data-test-locator="ValueProposition Link"
                    >
                      {linkTitle}
                    </Typography.Paragraph>
                  </Link>
                )}
              </Grid>
              <Grid item xs={6}>
                <Paper
                  className={cx(
                    classes.paper,
                    isOdd ? classes.paperOdd : classes.paperEven,
                  )}
                >
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    className={classes.imgContainer}
                  >
                    <div>
                      <LazyComponent
                        bottomOffset="-200px"
                        scrollableAncestor="window"
                      >
                        <ContentfulImg
                          data-test-locator="ValueProposition Image"
                          src={image.fields.url}
                          alt={alt}
                          defaultSrc={image.fields.url}
                          sizes={{ md: '400px' }}
                        />
                      </LazyComponent>
                    </div>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          );
        },
      )}
    </Grid>
  );
};
